/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Vega resources container
 *
 * @file ResourceContainer.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getSnapshot } from 'mobx-state-tree';
import { inject, observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';

import FilterComponent from '../components/filter/FilterComponent';
import ResourceDataContainer from './ResourceDataContainer';
import ResourceMenuContainer from './ResourceMenuContainer';
import ResourceUtils from '../utils/ResourceUtils';
import SearchComponenet from '../components/search/SearchComponent';
import SelectComponent from '../components/select/SelectComponent';
import UserBaseTabContainer from './UserBaseTabContainer';
import StoreRegistry from '../common/StoreRegistry';
import {
  BOOK_LEVEL_RESOURCE,
  BULK_DOWNLOAD_STATUS_COMPLETED,
  BULK_DOWNLOAD_STATUS_REMOVING,
  CONTENT_TYPE_DATA_CONNECTION,
  CONTENT_TYPE_KEYS,
  DOWNLOAD_STATUS_COMPLETED,
  DOWNLOAD_STATUS_DELETING,
  DOWNLOAD_STATUS_ERROR,
  DOWNLOAD_STATUS_INPROGRESS,
  EMPTY_RESOURCE,
  PAGE_LEVEL_RESOURCE,
  USER_ROLE_INSTRUCTOR,
  USER_ROLE_STUDENT
} from '../common/constants';

import '../assets/styles/style.scss';

const styles = (theme) => ({
  stickyHeader: {
    position: 'sticky !important',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.color.background,
    paddingBottom: 18
  }
});

class ResourceContainer extends Component {
  constructor(props) {
    super(props);
    this.stores = StoreRegistry.getStores();
    this.initialize();
    this.state = {
      isFilterDropDownOpen: false
    };
  }

  initialize = () => {
    const {
      bookId,
      resourceData,
      resources,
      selectedResources,
      showDownload,
      userRole
    } = this.props;

    if (resourceData && resourceData !== EMPTY_RESOURCE) {
      const {
        resources: {
          setUserRoleTab,
          setShowTeacherTab,
          setShowStudentTab
        }
      } = this.stores;
      const flatResources = ResourceUtils.constructFlatResource(resourceData);
      const showTeacherTab = flatResources.some(resource => resource.limitByRoles && resource.limitByRoles.includes(USER_ROLE_INSTRUCTOR));
      const showStudentTab = flatResources.some(resource => resource.limitByRoles && resource.limitByRoles.includes(USER_ROLE_STUDENT));
      setShowTeacherTab(showTeacherTab);
      setShowStudentTab(showStudentTab);
      if (ResourceUtils.isStudentRoleTab(resources.showTeacherTab, resources.showStudentTab, userRole)) {
        setUserRoleTab(USER_ROLE_STUDENT);
      }
      resources.setStructuredResourcesData(resourceData, showDownload, selectedResources);
    } else {
      resources.fetch(bookId, userRole);
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      resources,
      showDownload
    } = this.props;
    // You don't have to do this check first, but it can help prevent an unneeded render
    if (nextProps.progressCount !== this.props.progressCount) {
      resources.setStructuredResourcesData(nextProps.resourceData, showDownload, nextProps.selectedResources);
    }
  }

  /**
   * Handles bulk download change
   *
   * @param {*} status
   */
  handleBulkDownloadStatusChange = (status) => {
    const { bulkDownloadStatusCallback, resources } = this.props;

    bulkDownloadStatusCallback(status, {
      filteredContentType: [...getSnapshot(resources.filteredContentType)],
      filterOn: resources.filterOn,
      isSearchOn: resources.isSearchOn,
      resourceLevelTab: resources.resourceLevelTab,
      searchedKeyword: resources.searchedKeyword,
      userRoleTab: resources.userRoleTab
    });
  };

  /**
   * Handles cancel click
   */
  handleCancel = () => {
    const { cancelCallback, resources } = this.props;

    resources.setBulkDownloadStatus(BULK_DOWNLOAD_STATUS_COMPLETED);
    resources.setSelectedResources([], true);
    cancelCallback();
  };

  /**
   * Handles download button click
   */
  handleDownload = () => {
    const { downloadCallback, resources } = this.props;
    const selectedResources = [...getSnapshot(resources.selectedResources)];
    const currnetSelectedResources = selectedResources.map(resource => ({ ...resource, current: true }));

    downloadCallback(currnetSelectedResources);
  };

  /**
   * Handles filter action click
   * @param {*} values
   */
  handleFilterActionClick = (values) => {
    const { resources } = this.props;

    resources.setFilteredContentType(values);
  };

  /**
   * Handles filter drop down open and close state
   * @param {*} state
   */
  handleFilterDropDownOpen = (state) => {
    this.setState({
      isFilterDropDownOpen: state
    });
  };

  /**
   * Handles filter on and off
   */
  handleFilterOn = () => {
    const { resources } = this.props;

    resources.setFilterOn(!resources.filterOn);
    resources.setFilteredContentType([]);
  };

  /**
   * Handles search on and off
   *
   */
  handleKeywordChange = (event) => {
    const searchedKeyword = event.target.value;
    const { resources } = this.props;

    resources.setSearchedKeyword(searchedKeyword);
  };

  /**
   * Handles remove button click
   */
  handleRemove = () => {
    const { removeCallback, resources } = this.props;
    const selectedResources = [...getSnapshot(resources.selectedResources)];
    const currnetSelectedResources = selectedResources.map(resource => ({ ...resource, current: true }));

    resources.setBulkDownloadStatus(BULK_DOWNLOAD_STATUS_REMOVING);
    removeCallback(currnetSelectedResources);
  };

  /**
  * Get data based on current user and current tab
  *
  * @param {string} currentTab - Book/Page
  */
  handleResourceLevelTabChange = (currentTab) => {
    let userRoleTabSelected = BOOK_LEVEL_RESOURCE;

    if (currentTab === 1) {
      userRoleTabSelected = PAGE_LEVEL_RESOURCE;
    }
    this.props.resources.setResourceLevelTab(userRoleTabSelected);
  };

  /**
   * Handles retry button click
   */
  handleRetry = () => {
    const { retryCallback, resources } = this.props;
    const retryCount = resources.retryCount;
    const selectedResources = [...getSnapshot(resources.selectedResources)];
    const currnetSelectedResources = selectedResources.map(resource => {
      if (resource.status && resource.status === DOWNLOAD_STATUS_ERROR) {
        return ({ ...resource, current: true });
      }

      return ({ ...resource, current: false });
    });

    if (retryCount > 1) {
      // resources.setRetryCount(0);
    } else {
      resources.setRetryCount(retryCount + 1);
    }
    retryCallback(currnetSelectedResources);
  };

  /**
   * Handles cancel button click during retry
   */
  handleRetryCancel = () => {
    const { resources, retryCancelCallback } = this.props;

    resources.setRetryCount(0);
    resources.setBulkDownloadStatus(BULK_DOWNLOAD_STATUS_COMPLETED);
    resources.setSelectedResources([], true);
    retryCancelCallback();
  };

  /**
   * Handles search on and off
   *
   */
  handleSearchOn = () => {
    const { resources } = this.props;

    resources.setIsSearchOn(!resources.isSearchOn);
    resources.setSearchedKeyword('');
  };

  /**
   * Get data based on current user and current tab
   *
   * @param {string} currentTab - Book/Page
   */
  handleUserRoleTabChange = (currentTab) => {
    let userRoleTabSelected = USER_ROLE_STUDENT;

    if (currentTab === 1) {
      userRoleTabSelected = USER_ROLE_INSTRUCTOR;
    }
    this.props.resources.setUserRoleTab(userRoleTabSelected);
  };


  render() {
    const {
      classes,
      currentPageId,
      isCardView,
      onResourceClick,
      progressCount,
      resources,
      showDownload,
      userRole
    } = this.props;
    const resourceFlatData = getSnapshot(resources.resourceFlatData);
    const structuredResourceData = getSnapshot(resources.structuredResourceData);
    const selectedResources = getSnapshot(resources.selectedResources);
    const selectAll = resources.selectAll;
    const {
      filterOn,
      isSearchOn,
      searchedKeyword
    } = this.props.resources;
    const filteredContentType = getSnapshot(resources.filteredContentType);
    const { isFilterDropDownOpen } = this.state;
    const allContentType = CONTENT_TYPE_DATA_CONNECTION.map(
      contentTypeData => contentTypeData[CONTENT_TYPE_KEYS.TYPE]);
    const hasDownloadedResource = resourceFlatData.some(resource => resource.status === DOWNLOAD_STATUS_COMPLETED);
    const inprogressResources = this.props.selectedResources &&
    this.props.selectedResources.filter(resource => resource.status === DOWNLOAD_STATUS_INPROGRESS && resource.current);
    const deletingResources = this.props.selectedResources &&
    this.props.selectedResources.filter(resource => resource.status === DOWNLOAD_STATUS_DELETING && resource.current);
    const currentSelectedResources = this.props.selectedResources &&
    this.props.selectedResources.filter(resource => resource.current);
    const progressResource = resources.bulkDownloadStatus === BULK_DOWNLOAD_STATUS_REMOVING
      ? deletingResources : inprogressResources;

    if (!resources.isFetched) {
      return (
        <CircularProgress
          className="resource-loader"
        />
      );
    }

    return (
      <Fragment>
        <div className={classes.stickyHeader}>
          <div className="resource-header-container">
            {
              userRole === USER_ROLE_INSTRUCTOR &&
              (<UserBaseTabContainer
                bulkDownloadStatus={resources.bulkDownloadStatus}
                handleTabChange={this.handleUserRoleTabChange}
                tabValue={resources.userRoleTab === USER_ROLE_STUDENT ? 0 : 1}
                showTeacherTab={resources.showTeacherTab}
                showStudentTab={resources.showStudentTab}
              />)
            }
          </div>
          <div className="resource-menu-container">
            <ResourceMenuContainer
              bulkDownloadStatus={resources.bulkDownloadStatus}
              handleFilterOn={this.handleFilterOn}
              handleSearchOn={this.handleSearchOn}
              handleTabChange={this.handleResourceLevelTabChange}
              hasBookResource={resources.isBookResource}
              hasPageResource={resources.isPageResource}
              isCurrentTabEmpty={resources.isCurrentTabEmpty}
              isFilterOn={filterOn}
              isSearchOn={isSearchOn}
              tabValue={resources.resourceLevelTab === BOOK_LEVEL_RESOURCE ? 0 : 1}
            />
          </div>

          {filterOn &&
            <div className="resource-filter-container">
              <FilterComponent
                availableContentType={ResourceUtils.getAvailableContentData(resourceFlatData)}
                bulkDownloadStatus={resources.bulkDownloadStatus}
                filteredContentType={filteredContentType}
                isCurrentTabEmpty={resources.isCurrentTabEmpty}
                onFilterActionClick={this.handleFilterActionClick}
                onFilterDropDownOpen={this.handleFilterDropDownOpen}
              />
            </div>}
          {isSearchOn &&
            <div className="resource-search-container">
              <SearchComponenet
                bulkDownloadStatus={resources.bulkDownloadStatus}
                filteredResourceData={searchedKeyword !== '' ?
                  ResourceUtils.groupResourcesBasedOnContentType(resources.filteredResourceData, allContentType) : []}
                isCurrentTabEmpty={resources.isCurrentTabEmpty}
                onKeywordChange={this.handleKeywordChange}
                searchedKeyword={resources.searchedKeyword}
              />
            </div>
          }
          {showDownload &&
            <SelectComponent
              bulkDownloadStatus={resources.bulkDownloadStatus}
              bulkDownloadStatusCallback={this.handleBulkDownloadStatusChange}
              currentSelectedResources={currentSelectedResources}
              handleCancel={this.handleCancel}
              handleDownload={this.handleDownload}
              handleRemove={this.handleRemove}
              handleRetry={this.handleRetry}
              handleRetryCancel={this.handleRetryCancel}
              hasDownloadedResource={hasDownloadedResource}
              progressCount={progressResource.length}
              selectAll={selectAll}
              selectedResources={selectedResources}
              updateSelectedResource={resources.setSelectedResources}
            />
          }
        </div>
        <div className="resource-data-container">
          <ResourceDataContainer
            bulkDownloadStatus={resources.bulkDownloadStatus}
            currentPageId={currentPageId}
            filteredContentType={filterOn ? filteredContentType : []}
            isCardView={isCardView}
            isFilterDropDownOpen={isFilterDropDownOpen}
            onResourceClick={onResourceClick}
            progressCount={progressCount}
            resourceFlatData={resourceFlatData}
            resourceLevelTabValue={resources.resourceLevelTab}
            resourceStructuredData={structuredResourceData}
            searchedKeyword={searchedKeyword}
            selectedResources={selectedResources}
            setIsCurrentTabEmpty={resources.setIsCurrentTabEmpty}
            setIsBookResource={resources.setIsBookResource}
            setIsPageResource={resources.setIsPageResource}
            setResourceLevelTab={resources.setResourceLevelTab}
            setFilteredResourcesData={resources.setFilteredResourcesData}
            setSelectedResources={resources.setSelectedResources}
            showDownload={showDownload}
            userRoleTabValue={resources.userRoleTab}
          />
        </div>
      </Fragment>
    );
  }
}

ResourceContainer.propTypes = {
  bookId: PropTypes.string.isRequired,
  bulkDownloadStatusCallback: PropTypes.func.isRequired,
  cancelCallback: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  currentPageId: PropTypes.array.isRequired,
  downloadCallback: PropTypes.func.isRequired,
  isCardView: PropTypes.bool.isRequired,
  onResourceClick: PropTypes.func.isRequired,
  progressCount: PropTypes.number.isRequired,
  resourceData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ]),
  retryCancelCallback: PropTypes.func.isRequired,
  removeCallback: PropTypes.func.isRequired,
  resources: PropTypes.object,
  retryCallback: PropTypes.func.isRequired,
  selectedResources: PropTypes.array.isRequired,
  showDownload: PropTypes.bool.isRequired,
  userRole: PropTypes.string.isRequired
};

ResourceContainer.defaultProps = {
  resourceData: EMPTY_RESOURCE,
  resources: {}
};

export default withStyles(styles)(inject('resources')(observer(ResourceContainer)));
